import { useCallback, useEffect, useState } from 'react';
import { useAsk } from '../components/Ask';
import { api, ApiError } from '../lib/api';
import type { MentionablePerson, TaskComment } from '../lib/types';
import { initials } from './BoardCard';
import { MentionTextarea } from '../components/MentionTextarea';
import { formatDateTime, formatWhen } from '../lib/dates';
import { Linkified } from '../components/Linkified';

/**
 * The conversation on a task. Anyone who can change work on the project may comment, whether
 * or not the task is theirs — asking a question about someone else's task is the point.
 * Mentioning someone with @their-name notifies them, as does commenting on their task.
 */
export function CommentsPanel({
  itemId,
  canComment,
  focusReply,
}: {
  itemId: string;
  canComment: boolean;
  /**
   * Opens with the cursor already in the box.
   *
   * Set when somebody arrived from the "Reply" button in a mention email — they came here to
   * answer, and making them find the box first is the whole of what that button was meant to
   * save them.
   */
  focusReply?: boolean;
}) {
  const [comments, setComments] = useState<TaskComment[] | null>(null);
  const ask = useAsk();
  const [body, setBody] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editBody, setEditBody] = useState('');
  const [people, setPeople] = useState<MentionablePerson[]>([]);

  const load = useCallback(async () => {
    try {
      setComments(await api.get<TaskComment[]>(`/work-items/${itemId}/comments`));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load comments');
      setComments([]);
    }
  }, [itemId]);

  useEffect(() => {
    void load();
  }, [load]);

  // Who can be named here. The same list the API resolves mentions against, so the box
  // cannot offer somebody who would then not be notified.
  useEffect(() => {
    if (!canComment) return;
    api
      .get<MentionablePerson[]>(`/work-items/${itemId}/mentionable`)
      .then(setPeople)
      .catch(() => setPeople([]));
  }, [itemId, canComment]);

  async function post() {
    if (!body.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await api.post(`/work-items/${itemId}/comments`, { body: body.trim() });
      setBody('');
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not post that comment');
    } finally {
      setBusy(false);
    }
  }

  async function saveEdit(id: string) {
    if (!editBody.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await api.patch(`/comments/${id}`, { body: editBody.trim() });
      setEditingId(null);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save that comment');
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: string) {
    const sure = await ask.confirm({
      title: 'Delete this comment?',
      body: 'It disappears for everybody on the task.',
      confirmLabel: 'Delete it',
      danger: true,
    });
    if (!sure) return;
    setBusy(true);
    setError(null);
    try {
      await api.del(`/comments/${id}`);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not delete that comment');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card mt-2">
      <div className="card-head">
        <h3>Comments</h3>
        {comments && comments.length > 0 && (
          <span className="dim" style={{ fontSize: 12 }}>
            {comments.length}
          </span>
        )}
      </div>

      <div className="card-pad">
        {error && <div className="callout">{error}</div>}

        {comments === null ? (
          <span className="dim">Loading…</span>
        ) : comments.length === 0 ? (
          <p className="dim" style={{ margin: 0, fontSize: 13 }}>
            Nothing said yet.
          </p>
        ) : (
          <div className="stack" style={{ gap: 14 }}>
            {comments.map((comment) => (
              <div key={comment.id} className="row" style={{ gap: 10, alignItems: 'flex-start' }}>
                <span className="avatar" title={comment.author?.name ?? 'Unknown'}>
                  {comment.author ? initials(comment.author.name) : '?'}
                </span>
                <div className="grow" style={{ minWidth: 0 }}>
                  <div className="row" style={{ gap: 7 }}>
                    <span style={{ fontWeight: 600, fontSize: 13 }}>{comment.author?.name ?? 'Unknown'}</span>
                    <span className="dim" style={{ fontSize: 11 }}>
                      {formatWhen(comment.createdAt)}
                    </span>
                    {comment.editedAt && (
                      <span className="dim" style={{ fontSize: 11 }} title={formatDateTime(comment.editedAt)}>
                        · edited
                      </span>
                    )}
                    {comment.source !== 'hub' && <span className="badge">{comment.source}</span>}
                  </div>

                  {editingId === comment.id ? (
                    <>
                      <div style={{ marginTop: 6 }}>
                        <MentionTextarea
                          value={editBody}
                          onChange={setEditBody}
                          people={people}
                          onSubmit={() => void saveEdit(comment.id)}
                        />
                      </div>
                      <div className="row" style={{ gap: 6, marginTop: 6 }}>
                        <button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={() => saveEdit(comment.id)}>
                          Save
                        </button>
                        <button type="button" className="btn btn-quiet btn-sm" onClick={() => setEditingId(null)}>
                          Cancel
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      {/* Kept as plain text with newlines honoured; no markup is rendered. */}
                      <div style={{ whiteSpace: 'pre-wrap', marginTop: 2, fontSize: 13 }}><Linkified text={comment.body} /></div>
                      {(comment.canEdit || comment.canDelete) && (
                        <div className="row" style={{ gap: 4, marginTop: 4 }}>
                          {comment.canEdit && (
                            <button
                              type="button"
                              className="btn btn-quiet btn-sm"
                              style={{ paddingLeft: 0 }}
                              onClick={() => {
                                setEditingId(comment.id);
                                setEditBody(comment.body);
                              }}
                            >
                              Edit
                            </button>
                          )}
                          {comment.canDelete && (
                            <button type="button" className="btn btn-quiet btn-sm" disabled={busy} onClick={() => remove(comment.id)}>
                              Delete
                            </button>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {canComment ? (
          <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--line)' }}>
            <MentionTextarea
              value={body}
              onChange={setBody}
              people={people}
              onSubmit={() => void post()}
              autoFocus={focusReply}
              placeholder="Add a comment. Type @ to notify someone."
            />
            <div className="row-between" style={{ marginTop: 7 }}>
              <span className="dim" style={{ fontSize: 11 }}>
                Enter to send, Shift+Enter for a new line. Type @ to name someone.
              </span>
              <button type="button" className="btn btn-primary btn-sm" disabled={busy || !body.trim()} onClick={post}>
                {busy ? 'Posting…' : 'Comment'}
              </button>
            </div>
          </div>
        ) : (
          <p className="dim" style={{ fontSize: 12, marginTop: 14, marginBottom: 0 }}>
            Your role can read this conversation but not add to it.
          </p>
        )}
      </div>
    </div>
  );
}
